import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import Mark from 'mark.js';
import { Emitter } from '@patsnap/tahiti-core';
import scrollIntoView from 'scroll-into-view-if-needed';
import { GetClaimCompareMatchRepository, GetResultPositionRepository } from '../claim-charting-repository';
import { FeatureLocatedModel } from '../../models/feature-located-model';
import { CLAIM_CHARTING_MARK_CLASS_NAME, COMPARE_ORIGINAL_WRAPPER_CLASS_NAME } from '../../constants';
import type { FeatureLocatedState, GetClaimCompareMatchParams, GetResultPositionParams } from '../../types';
import type { FeatureLocatedService } from '../feature-located-service';

@injectable()
export class FeatureLocatedServiceImpl implements FeatureLocatedService {
    private _onStatesChangeEmitter = new Emitter<FeatureLocatedState>();
    readonly onStatesChange = this._onStatesChangeEmitter.event;

    constructor(
        @inject(GetClaimCompareMatchRepository)
        private _getClaimFeatureRepository: GetClaimCompareMatchRepository,
        @inject(GetResultPositionRepository)
        private _getResultPositionRepository: GetResultPositionRepository,
        @inject(FeatureLocatedModel)
        private _model: FeatureLocatedModel,
    ) {
        this._model.onStatesChange(this._emitModelDataChange.bind(this));
    }

    private _emitModelDataChange() {
        this._onStatesChangeEmitter.fire(this.getModelData());
    }

    public getModelData() {
        return this._model.modelData;
    }

    public updateModelData(data: Partial<FeatureLocatedState>) {
        this._model.update(() => ({
            ...this._model.modelData,
            ...data,
        }));
    }

    public setLocatedPatentInfo({ patentId, text }: { patentId: string; text: string; }) {
        this.updateModelData({
            patentId,
            originText: text,
        });
    }

    public async getFeatureMatchData(params: GetClaimCompareMatchParams): Promise<void> {
        const data = await this._getClaimFeatureRepository.getClaimCompareMatch(params);
        this.updateModelData({
            matchText: data.matchText,
        });
    }

    public async getFeatureMatchDataV2(params: GetResultPositionParams): Promise<void> {
        const data = await this._getResultPositionRepository.getResultPosition(params);
        this.updateModelData({
            matchText: data.matchText,
            originText: data.originalText,
        });
    }

    private _clearNodeGroups(): void {
        this._model.update(() => ({
            ...this._model.modelData,
            nodeGroups: [],
            currentGroup: 1,
            totalMatches: 0,  // 清空时重置总匹配数
        }));
    }

    /**
     * 标准化 matchText 为关键词数组
     * @param matchText - 可能是字符串（单行或\n分隔的多行）或字符串数组
     * @returns 过滤空值后的关键词数组
     */
    private _normalizeMatchText(matchText: string | string[]): string[] {
        const textArray = Array.isArray(matchText) ? matchText : matchText.split('\n');
        return textArray.map(k => k.trim()).filter(k => k.length > 0);
    }

    /**
     * 判断两个高亮节点是否相邻（属于同一个逻辑匹配）
     * 用于 acrossElements: true 时，区分同一个匹配的多个节点 vs 不同匹配
     */
    private _isAdjacentMark(lastNode: HTMLElement, currentNode: HTMLElement): boolean {
        try {
            // 使用 Range API 获取两个节点之间的文本内容
            const range = document.createRange();
            range.setStartAfter(lastNode);
            range.setEndBefore(currentNode);
            const textBetween = range.toString();

            // 如果之间的文本为空或只有空白，认为是相邻的（同一个逻辑匹配）
            return textBetween.trim().length === 0;
        } catch {
            // 如果 Range 创建失败（比如节点不在同一个父元素），认为不相邻
            return false;
        }
    }

    public markFeatureInDocument(wrapperClass?: string): void {
        const wrapper = document.querySelector<HTMLElement>(`.${wrapperClass || COMPARE_ORIGINAL_WRAPPER_CLASS_NAME}`);
        if (!wrapper) return;

        this._clearNodeGroups();

        const instance = new Mark(wrapper);

        // 先清除旧的高亮标记，避免累积显示
        instance.unmark({
            className: CLAIM_CHARTING_MARK_CLASS_NAME,
            done: () => {
                const matchText = this.getModelData().matchText;
                const keywords = this._normalizeMatchText(matchText);

                if (keywords.length === 0) {
                    return;
                }

                // 记录所有高亮位置，每个逻辑匹配一个导航目标
                const groups: HTMLElement[] = [];
                let completedCount = 0;

                keywords.forEach((keyword) => {
                    let lastNode: HTMLElement | null = null;

                    instance.mark(keyword, {
                        className: CLAIM_CHARTING_MARK_CLASS_NAME,
                        separateWordSearch: false,
                        caseSensitive: true,
                        acrossElements: true,
                        each: (node: HTMLElement) => {
                            // 判断是否为新的逻辑匹配（不是跨元素的同一个匹配）
                            if (!lastNode || !this._isAdjacentMark(lastNode, node)) {
                                groups.push(node);
                            }
                            lastNode = node;
                        },
                        done: () => {
                            completedCount++;

                            // 所有 keywords 都高亮完成后，更新 model
                            if (completedCount === keywords.length) {
                                // 按 DOM 顺序排序，确保导航顺序与文档阅读顺序一致
                                // 因为多个 keyword 的 mark 操作是并行执行的，groups 顺序可能是乱的
                                groups.sort((a, b) => {
                                    const position = a.compareDocumentPosition(b);
                                    // DOCUMENT_POSITION_FOLLOWING (4): b 在 a 之后
                                    if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
                                    // DOCUMENT_POSITION_PRECEDING (2): b 在 a 之前
                                    if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
                                    return 0;
                                });

                                this._model.update(() => ({
                                    ...this._model.modelData,
                                    nodeGroups: groups,
                                    totalMatches: groups.length,
                                }));
                                this.scrollToNode();
                            }
                        },
                    });
                });
            },
        });
    }

    public scrollToNode(number: number = this._model.modelData.currentGroup): void {
        const node = this._model.modelData.nodeGroups[number - 1];
        if (!node) return;

        // 使用 scroll-into-view-if-needed 库，自动识别最近的滚动容器
        // 使用 'center' 让高亮文本居中显示，视觉效果更好
        scrollIntoView(node, {
            scrollMode: 'if-needed',
            block: 'center',
            inline: 'center',
            behavior: 'smooth',
        });

        this.updateModelData({
            currentGroup: number,
        });
    }
}
